import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { ToolStatusView, ToolTone } from "../types";

const TONE_CLASS: Record<ToolTone, string> = {
  neutral: "bg-[#f0f2f1] text-[#385047]",
  info: "bg-[#dde9ff] text-[#2747c7]",
  warning: "bg-[#fff0e6] text-[#a84a12]",
  success: "bg-[#e3f4ec] text-[#156548]",
  danger: "bg-[#fde8ec] text-[#b01a33]",
};

/** A tool's status. Always carries its words: colour is never the only signal. */
export function ToolStatusPill({ status }: { status: Pick<ToolStatusView, "label" | "tone"> }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
        TONE_CLASS[status.tone],
      )}
    >
      {status.label}
    </span>
  );
}

export function ToolIcon({ name, logoUrl }: { name: string; logoUrl?: string | null }) {
  if (logoUrl) {
    return <img src={logoUrl} alt="" className="size-9 shrink-0 rounded-lg border border-[#e6e8e7] object-contain" />;
  }
  return (
    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#e3f4ec] text-[14px] font-semibold text-[#156548]">
      {name.trim().charAt(0).toUpperCase() || "T"}
    </div>
  );
}

export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Could not copy");
        }
      }}
      className="inline-flex h-7 items-center gap-1 rounded-md border border-[#e6e8e7] bg-white px-2 text-[12px] text-[#385047] hover:bg-[#f8fbf9]"
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      {copied ? "Copied" : label}
    </button>
  );
}
